// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Automatic shot marking: microphone impact detection gated by GPS stationarity.
// ANDROID PHONE ONLY — every entry point below no-ops elsewhere (see isSupported):
// web cannot hold the microphone across tab suspension, and iOS needs its own
// consent copy, Info.plist strings and an App Store conversation before the mic
// can run through a round.
//
// The microphone is duty-cycled by movement, which is three wins in one: the
// battery only pays while the player stands still (address + swing), pocket
// rustle while walking — the classic false trigger — is never even heard, and
// the throwaway recording files stay seconds long instead of round long.
//
// PRIVACY: no audio ever leaves the device. Recording exists only because the
// platform's metering API requires an active recording; files are deleted the
// moment each listening window closes, and only detection timestamps survive.

import * as Location from 'expo-location';
import { Platform } from 'react-native';

import {
  emitShotTrackingDebug,
  hasShotTrackingDebugListeners,
} from '../features/shot-tracking/debugBus';
import {
  createImpactAudioDetector,
  IMPACT_AUDIO_DEFAULTS,
  type ImpactAudioDetector,
} from '../features/shot-tracking/impactAudio';
import { createEventId } from './offlineQueue';
import { combineFixes, isStationary, type ClusterFit, type ClusterFix } from './positionCluster';

/** One fix per 3s: enough to resolve "standing at the ball" from "walking". */
const GPS_INTERVAL_MS = 3000;

/** How long the player must have been still before the microphone opens.
 *  Covers walking to a stop and settling over the ball. */
const STATIONARY_SUSTAIN_MS = 4000;

/** A fix worse than this is not attached to a mark; the server treats such
 *  marks as impacts without positions. */
const MAX_MARK_ACCURACY_M = 50;

/** Metering cadence for the impact detector. */
const METERING_INTERVAL_MS = 100;

/** A rise this size that did NOT trigger is worth a debug log entry — putts
 *  and chips live in this band, and calibration needs to see them. */
const NEAR_MISS_RISE_DB = 8;

/** One putt never sounds twice inside this window; echoes fold into it. */
const PUTT_MARK_REFRACTORY_MS = 2500;

/** Never start below this battery level; shot marks are not worth a dead phone. */
export const MIN_BATTERY_FRACTION = 0.2;

export type ShotMark = {
  markId: string;
  /** ISO-8601 device time of the impact. */
  timestamp: string;
  /** A full swing, or a putt — putts are counted server-side, never measured. */
  kind: 'stroke' | 'putt';
  lat: number | null;
  lon: number | null;
  accuracyMeters: number | null;
  holeNumber: number | null;
};

export type StartShotTrackingOptions = {
  roundId: string;
  /** The device owner's player id — marks are only ever the phone owner's. */
  playerId: string;
  /** Whether the player has opted in. Tracking never starts without this. */
  hasConsent: boolean;
  /** Battery level 0..1, or null when unknown. */
  batteryFraction: number | null;
  /** Called for every detected impact while the player is stationary. */
  onMark: (mark: ShotMark) => void;
};

export type StartShotTrackingResult =
  | 'started'
  | 'unsupported-platform'
  | 'no-consent'
  | 'battery-too-low'
  | 'location-permission-denied'
  | 'microphone-permission-denied'
  | 'already-running';

export const isSupported = (): boolean => Platform.OS === 'android';

type RecentFix = ClusterFix;

type RecorderHandle = {
  stop: () => Promise<void>;
};

type Session = {
  roundId: string;
  playerId: string;
  holeNumber: number | null;
  onMark: (mark: ShotMark) => void;
  detector: ImpactAudioDetector;
  fixes: RecentFix[];
  stationarySince: number | null;
  subscription: Location.LocationSubscription | null;
  recorder: RecorderHandle | null;
  /** Serializes recorder starts/stops; both are async and GPS keeps firing. */
  recorderBusy: boolean;
  /**
   * Putt-test mode (developer debug page): the microphone is held open
   * regardless of stationarity — indoors GPS delivers no fixes, so the natural
   * gate never opens — and detected impacts are logged but NOT sent as shot
   * marks, so a calibration session on a practice green or a carpet never
   * dirties the round it borrowed.
   */
  puttTestMode: boolean;
  /** Refractory marker for putt-class marks (detector clock, ms). */
  lastPuttMarkAt: number;
};

let session: Session | null = null;

// The stationarity rule and the great-circle helper live in ./positionCluster
// alongside the averaging that consumes them — the hole view needs the same two
// and must not import this module (expo-av, Platform) to get them. Re-exported
// because both are part of this module's tested surface.
export { haversineMeters, isStationary } from './positionCluster';

export const startShotTracking = async (
  options: StartShotTrackingOptions
): Promise<StartShotTrackingResult> => {
  if (!isSupported()) return 'unsupported-platform';
  if (!options.hasConsent) return 'no-consent';
  if (options.batteryFraction !== null && options.batteryFraction < MIN_BATTERY_FRACTION) {
    return 'battery-too-low';
  }
  if (session) return 'already-running';

  const location = await Location.requestForegroundPermissionsAsync();
  if (location.status !== 'granted') return 'location-permission-denied';

  const microphone = await requestMicrophonePermission();
  if (!microphone) return 'microphone-permission-denied';

  // Per-session throttle for near-miss logging: one putt produces a couple of
  // adjacent samples in the near-miss band, which should read as one sound.
  let lastNearMissAt = 0;

  const active: Session = {
    roundId: options.roundId,
    playerId: options.playerId,
    holeNumber: null,
    onMark: options.onMark,
    detector: createImpactAudioDetector({
      onDiagnostic: (info) => {
        const inPuttBand =
          info.rise >= NEAR_MISS_RISE_DB && info.rise < IMPACT_AUDIO_DEFAULTS.minRiseDb;
        if (hasShotTrackingDebugListeners()) {
          emitShotTrackingDebug({
            kind: 'metering',
            at: info.ts,
            db: info.db,
            baseline: info.baseline,
            rise: info.rise,
          });
          if (inPuttBand && info.ts - lastNearMissAt >= 1000) {
            lastNearMissAt = info.ts;
            emitShotTrackingDebug({ kind: 'nearMiss', at: info.ts, riseDb: info.rise });
          }
        }
        // Production putt marking runs with or without a debug page open.
        if (inPuttBand) {
          onPuttCandidate(info.ts);
        }
      },
    }),
    fixes: [],
    stationarySince: null,
    subscription: null,
    recorder: null,
    recorderBusy: false,
    puttTestMode: false,
    lastPuttMarkAt: 0,
  };
  session = active;
  emitShotTrackingDebug({ kind: 'session', at: Date.now(), message: 'started' });

  active.subscription = await Location.watchPositionAsync(
    {
      accuracy: Location.Accuracy.High,
      timeInterval: GPS_INTERVAL_MS,
      distanceInterval: 0,
    },
    (position) => {
      onFix(active, position);
    }
  );

  return 'started';
};

export const setShotTrackingHole = (holeNumber: number | null): void => {
  if (session) session.holeNumber = holeNumber;
};

export const stopShotTracking = async (): Promise<void> => {
  const active = session;
  if (!active) return;
  session = null;

  emitShotTrackingDebug({ kind: 'session', at: Date.now(), message: 'stopped' });
  active.subscription?.remove();
  active.subscription = null;
  await closeMicrophone(active);
};

/**
 * Runs the real impact path — fix attachment, mark emission, send-or-queue —
 * without a swing. The developer debug page's way of testing the pipeline
 * from a couch. Returns false when no session is running.
 */
export const simulateImpactForDebug = (): boolean => {
  const active = session;
  if (!active) return false;
  emitShotTrackingDebug({ kind: 'impact', at: Date.now(), riseDb: 0 });
  onImpact(active);
  return true;
};

/**
 * Putt-test mode for the developer debug page: holds the microphone open (the
 * stationarity gate needs GPS fixes, which indoors never come) and suppresses
 * outbound marks so detections during the test cannot touch the round.
 * Returns false when no capture session is running.
 */
export const setPuttTestModeForDebug = (enabled: boolean): boolean => {
  const active = session;
  if (!active) return false;
  active.puttTestMode = enabled;
  emitShotTrackingDebug({
    kind: 'session',
    at: Date.now(),
    message: enabled ? 'putt test started' : 'putt test stopped',
  });
  if (enabled && !active.recorder) {
    void openMicrophone(active);
  } else if (!enabled && active.recorder) {
    // The natural stationarity gate takes back over on the next GPS fix;
    // closing here keeps indoor sessions from listening forever.
    void closeMicrophone(active);
  }
  return true;
};

export const isShotTrackingRunning = (): boolean => session !== null;

const onFix = (active: Session, position: Location.LocationObject): void => {
  if (session !== active) return;

  const now = Date.now();
  active.fixes.push({
    lat: position.coords.latitude,
    lon: position.coords.longitude,
    accuracy: position.coords.accuracy ?? null,
    speed: position.coords.speed ?? null,
    ts: position.timestamp || now,
  });
  if (active.fixes.length > 10) {
    active.fixes = active.fixes.slice(-10);
  }

  const stationary = isStationary(active.fixes, now);
  if (stationary) {
    if (active.stationarySince === null) active.stationarySince = now;
  } else {
    active.stationarySince = null;
  }

  const listen = active.puttTestMode
    || (stationary && active.stationarySince !== null
      && now - active.stationarySince >= STATIONARY_SUSTAIN_MS);

  if (hasShotTrackingDebugListeners()) {
    const latest = active.fixes[active.fixes.length - 1];
    emitShotTrackingDebug({
      kind: 'fix',
      at: now,
      lat: latest.lat,
      lon: latest.lon,
      accuracy: latest.accuracy,
      speed: latest.speed,
      stationary,
      listening: listen,
    });
  }

  if (listen && !active.recorder) {
    void openMicrophone(active);
  } else if (!listen && active.recorder) {
    void closeMicrophone(active);
  }
};

const onImpact = (active: Session): void => {
  if (session !== active) return;

  // Putt-test sessions log detections (the recorder callback already emitted
  // the impact event) but never emit marks: calibration must not score.
  if (active.puttTestMode) return;

  emitMark(active, 'stroke');
};

/**
 * A sound in the putt band — clearly above baseline yet below the stroke
 * threshold — while the microphone is open (which means the player stands
 * still). Emitted as a putt-class mark: counted per hole server-side, kept
 * entirely apart from the measured shot sequence.
 */
const onPuttCandidate = (ts: number): void => {
  const active = session;
  if (!active || active.puttTestMode) return;
  if (ts - active.lastPuttMarkAt < PUTT_MARK_REFRACTORY_MS) return;
  active.lastPuttMarkAt = ts;
  emitMark(active, 'putt');
};

const emitMark = (active: Session, kind: ShotMark['kind']): void => {
  const fix = markFix(active);
  const mark: ShotMark = {
    markId: createEventId(),
    timestamp: new Date().toISOString(),
    kind,
    lat: fix?.lat ?? null,
    lon: fix?.lon ?? null,
    accuracyMeters: fix?.accuracy ?? null,
    holeNumber: active.holeNumber,
  };
  emitShotTrackingDebug({
    kind: 'mark',
    at: Date.now(),
    markId: mark.markId,
    source: 'phone-audio',
    markKind: kind,
    lat: mark.lat,
    lon: mark.lon,
    accuracyMeters: mark.accuracyMeters,
    holeNumber: mark.holeNumber,
    sampleCount: fix?.sampleCount,
  });
  active.onMark(mark);
};

/**
 * The position to attach to a mark: the average of the fixes taken while the
 * player stood over the ball, which is a better estimate than any one of them
 * ({@link combineFixes} for how much better, and why not more).
 *
 * Scoped to the standing stretch rather than the whole buffer. The buffer holds
 * ten fixes — 30 s at this sampling rate — which reaches back into the walk to
 * the ball; averaging that in would place the mark somewhere along the fairway
 * the player crossed.
 */
const markFix = (active: Session): ClusterFit | null => {
  const since = active.stationarySince;
  const cluster =
    since === null
      ? // No standing stretch (putt-test mode indoors): nothing to average.
        active.fixes.slice(-1)
      : // One sampling interval of grace: the fix that established stationarity
        // was taken just before the clock started on it.
        active.fixes.filter((fix) => fix.ts >= since - GPS_INTERVAL_MS);

  return combineFixes(cluster, MAX_MARK_ACCURACY_M);
};

const requestMicrophonePermission = async (): Promise<boolean> => {
  try {
    const av = loadExpoAv();
    if (!av) return false;
    const response = await av.Audio.requestPermissionsAsync();
    return response.status === 'granted';
  } catch {
    return false;
  }
};

const openMicrophone = async (active: Session): Promise<void> => {
  if (active.recorderBusy || active.recorder || session !== active) return;
  active.recorderBusy = true;
  try {
    // Metering reaches the debug bus enriched (baseline + rise) through the
    // detector's onDiagnostic, so nothing is emitted per-sample here.
    const recorder = await startMeteringRecorder((db) => {
      const event = active.detector.feed({ ts: Date.now(), db });
      if (event) {
        emitShotTrackingDebug({ kind: 'impact', at: event.ts, riseDb: event.riseDb });
        onImpact(active);
      }
    });
    if (session !== active) {
      await recorder?.stop();
      return;
    }
    active.recorder = recorder;
    if (recorder) {
      emitShotTrackingDebug({ kind: 'mic', at: Date.now(), open: true });
    } else {
      // expo-av missing or its API shape moved — without this line the debug
      // page shows an empty meter with no explanation ("Ingen ljuddata").
      emitShotTrackingDebug({
        kind: 'session',
        at: Date.now(),
        message: 'mic open failed: audio module unavailable',
      });
    }
  } catch (error) {
    // No microphone this window; GPS keeps running and the next stationary
    // stretch tries again. Loud in the debug log, silent everywhere else —
    // a swallowed failure here is indistinguishable from "you never stood
    // still", which is exactly the misdiagnosis it caused in the field.
    emitShotTrackingDebug({
      kind: 'session',
      at: Date.now(),
      message: `mic open failed: ${error instanceof Error ? error.message : 'unknown error'}`,
    });
  } finally {
    active.recorderBusy = false;
  }
};

const closeMicrophone = async (active: Session): Promise<void> => {
  const recorder = active.recorder;
  active.recorder = null;
  active.detector.reset();
  if (!recorder) return;
  emitShotTrackingDebug({ kind: 'mic', at: Date.now(), open: false });
  try {
    await recorder.stop();
  } catch {
    // The recorder is being discarded either way.
  }
};

// ---------------------------------------------------------------------------
// expo-av adapter. Loaded lazily and defensively (same reasoning as the tile
// cache): the API surface has moved between SDKs, and a mismatch must degrade
// to "no audio detection" rather than crashing a live round.

type ExpoAvModule = {
  Audio: {
    requestPermissionsAsync: () => Promise<{ status: string }>;
    Recording: new () => {
      prepareToRecordAsync: (options: Record<string, unknown>) => Promise<unknown>;
      setProgressUpdateInterval: (ms: number) => void;
      setOnRecordingStatusUpdate: (
        cb: (status: { isRecording?: boolean; metering?: number }) => void
      ) => void;
      startAsync: () => Promise<unknown>;
      stopAndUnloadAsync: () => Promise<unknown>;
      getURI: () => string | null;
    };
  };
};

const loadExpoAv = (): ExpoAvModule | null => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const av = require('expo-av') as ExpoAvModule;
    return av?.Audio ? av : null;
  } catch {
    return null;
  }
};

/** Smallest recording expo-av will accept: metering is all that is wanted. */
const RECORDING_OPTIONS = {
  isMeteringEnabled: true,
  android: {
    extension: '.m4a',
    outputFormat: 2, // MPEG_4
    audioEncoder: 3, // AAC
    sampleRate: 16000,
    numberOfChannels: 1,
    bitRate: 24000,
  },
  ios: {
    extension: '.m4a',
    audioQuality: 0,
    sampleRate: 16000,
    numberOfChannels: 1,
    bitRate: 24000,
    linearPCMBitDepth: 16,
    linearPCMIsBigEndian: false,
    linearPCMIsFloat: false,
  },
  web: {},
};

const startMeteringRecorder = async (
  onMetering: (db: number) => void
): Promise<RecorderHandle | null> => {
  const av = loadExpoAv();
  if (!av) return null;

  const recording = new av.Audio.Recording();
  await recording.prepareToRecordAsync(RECORDING_OPTIONS);
  recording.setProgressUpdateInterval(METERING_INTERVAL_MS);
  recording.setOnRecordingStatusUpdate((status) => {
    if (status?.isRecording && typeof status.metering === 'number') {
      onMetering(status.metering);
    }
  });
  await recording.startAsync();

  return {
    stop: async () => {
      try {
        await recording.stopAndUnloadAsync();
      } finally {
        deleteRecordingFile(recording.getURI());
      }
    },
  };
};

/** Deletes the throwaway recording file. Best effort — cache-dir files are
 *  reclaimable by the OS anyway; this just does it immediately. */
const deleteRecordingFile = (uri: string | null): void => {
  if (!uri) return;
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const fs = require('expo-file-system') as {
      File?: new (uri: string) => { exists: boolean; delete: () => void };
    };
    if (!fs?.File) return;
    const file = new fs.File(uri);
    if (file.exists) file.delete();
  } catch {
    // ignore
  }
};

export const __resetShotTrackingForTests = (): void => {
  session?.subscription?.remove();
  session = null;
};

export const __feedFixForTests = (
  fix: RecentFix
): void => {
  if (!session) return;
  onFix(session, {
    coords: {
      latitude: fix.lat,
      longitude: fix.lon,
      accuracy: fix.accuracy,
      speed: fix.speed,
      altitude: null,
      altitudeAccuracy: null,
      heading: null,
    },
    timestamp: fix.ts,
  } as Location.LocationObject);
};
